import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewModule as ViewModuleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getAllModules,
  createModule,
  updateModule,
  deleteModule,
} from '../services/moduleManagementService';
import ModuleManagementDialog from './ModuleManagementDialog';

/**
 * ModuleManagementPanel Component
 * Dialog showing list of modules with create, edit, delete actions
 */
const ModuleManagementPanel = ({ open, onClose }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleDialog, setModuleDialog] = useState({
    open: false,
    mode: 'create',
    module: null,
  });

  // Load modules
  const loadModules = async () => {
    try {
      setLoading(true);
      const response = await getAllModules();
      const modulesArray = response?.payload || [];
      setModules(modulesArray);
    } catch (error) {
      toast.error('Failed to load modules');
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    if (open) {
      loadModules();
    }
  }, [open]);

  // Handle create module
  const handleCreateModule = () => {
    setModuleDialog({
      open: true,
      mode: 'create',
      module: null,
    });
  };

  // Handle edit module
  const handleEditModule = (module) => {
    setModuleDialog({
      open: true,
      mode: 'edit',
      module,
    });
  };

  // Handle delete module
  const handleDeleteModule = async (module) => {
    if (!window.confirm(`Delete module "${module.ModuleName}"?\n\nThis will remove it from all permission configurations.`)) {
      return;
    }

    try {
      setSaving(true);
      await deleteModule(module.Id || module.id);
      toast.success('Module deleted successfully');
      await loadModules();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete module');
    } finally {
      setSaving(false);
    }
  };

  // Handle save module
  const handleSaveModule = async (moduleData) => {
    try {
      setSaving(true);
      
      // Transform data to match backend API requirements
      const apiData = {
        ModuleName: moduleData.name,
        DisplayName: moduleData.displayName || moduleData.name,
        Description: moduleData.description || '',
        Category: moduleData.category || 'Other',
        ParentModuleId: moduleData.parentModuleId || null,
      };
      
      if (moduleDialog.mode === 'create') {
        await createModule(apiData);
        toast.success('Module created successfully');
      } else {
        // Include ID in the body for update
        const moduleId = moduleData.Id || moduleData.id;
        await updateModule(moduleId, {
          ...apiData,
          Id: moduleId,
        });
        toast.success('Module updated successfully');
      }

      setModuleDialog({ open: false, mode: 'create', module: null });
      await loadModules();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 
        `Failed to ${moduleDialog.mode === 'create' ? 'create' : 'update'} module`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModuleDialog = () => {
    setModuleDialog({ open: false, mode: 'create', module: null });
  };

  // Filter modules based on search query
  const filteredModules = useMemo(() => {
    let filtered = modules;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = modules.filter(module => 
        module.ModuleName?.toLowerCase().includes(query) ||
        module.DisplayName?.toLowerCase().includes(query) ||
        module.Description?.toLowerCase().includes(query) ||
        module.Category?.toLowerCase().includes(query)
      );
    }

    // Sort to show parent modules before their children
    // First, separate into parent and child modules
    const parentModules = filtered.filter(m => !m.ParentModuleId);
    const childModules = filtered.filter(m => m.ParentModuleId);

    // Sort each group alphabetically
    parentModules.sort((a, b) => (a.ModuleName || '').localeCompare(b.ModuleName || ''));
    childModules.sort((a, b) => (a.ModuleName || '').localeCompare(b.ModuleName || ''));

    // Interleave: for each parent, add it and its children
    const organized = [];
    parentModules.forEach(parent => {
      organized.push(parent);
      // Add children of this parent
      const children = childModules.filter(c => c.ParentModuleId === (parent.Id || parent.id));
      organized.push(...children);
    });

    // Add any orphaned children (whose parents aren't in the filtered list)
    const addedChildIds = new Set(organized.filter(m => m.ParentModuleId).map(m => m.Id || m.id));
    childModules.forEach(child => {
      if (!addedChildIds.has(child.Id || child.id)) {
        organized.push(child);
      }
    });

    return organized;
  }, [modules, searchQuery]);

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          onClose();
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, maxHeight: '85vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ViewModuleIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', lineHeight: 1.2 }}>
                  Module Management
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Create, edit, and manage system modules
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Header with count and add button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Modules ({filteredModules.length}{searchQuery ? ` of ${modules.length}` : ''})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleCreateModule}
                  disabled={saving}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    },
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 1.5,
                  }}
                >
                  Add Module
                </Button>
              </Box>

              {/* Search field */}
              <TextField
                fullWidth
                placeholder="Search modules by name, category, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  },
                }}
              />

              {/* Info alert */}
              <Alert severity="info" sx={{ mb: 3, borderRadius: 1.5 }}>
                Modules define features that can be controlled through permissions. Each module can have View, Create, Edit, and Delete permissions.
              </Alert>

              {/* Modules list */}
              {filteredModules.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    color: 'text.secondary',
                  }}
                >
                  <ViewModuleIcon sx={{ fontSize: 60, opacity: 0.3, mb: 2 }} />
                  <Typography variant="h6">
                    {searchQuery ? 'No modules found' : 'No modules yet'}
                  </Typography>
                  <Typography variant="body2">
                    {searchQuery ? 'Try a different search term' : 'Create your first module to get started'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {filteredModules.map((module) => (
                    <Paper
                      key={module.Id || module.id}
                      sx={{
                        p: 2,
                        pl: module.ParentModuleId ? 4 : 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid',
                        borderColor: module.ParentModuleId ? '#ff980025' : 'divider',
                        borderLeft: module.ParentModuleId ? '4px solid #ff9800' : '1px solid',
                        borderRadius: 1.5,
                        bgcolor: module.ParentModuleId ? '#ff980005' : 'transparent',
                        '&:hover': {
                          borderColor: '#667eea',
                          bgcolor: '#f9fafb',
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          {module.ParentModuleId && (
                            <Typography
                              variant="caption"
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                bgcolor: '#ff980015',
                                color: '#ff9800',
                                borderRadius: 0.5,
                                fontWeight: 600,
                                fontSize: '0.65rem',
                              }}
                            >
                              SUB-MODULE
                            </Typography>
                          )}
                          <Typography variant="body1" fontWeight={600}>
                            {module.ModuleName}
                          </Typography>
                        </Box>
                        {module.ParentModuleId && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Parent: {modules.find(m => (m.Id || m.id) === module.ParentModuleId)?.ModuleName || 'Unknown'}
                          </Typography>
                        )}
                        {module.Description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {module.Description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          {module.Category && (
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.25,
                                bgcolor: '#667eea15',
                                color: '#667eea',
                                borderRadius: 0.75,
                                fontWeight: 600,
                              }}
                            >
                              {module.Category}
                            </Typography>
                          )}
                          {module.DisplayName && module.DisplayName !== module.ModuleName && (
                            <Typography variant="caption" color="text.secondary">
                              Display: {module.DisplayName}
                            </Typography>
                          )}
                          {/* Show child count if has children */}
                          {modules.filter(m => m.ParentModuleId === (module.Id || module.id)).length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.25,
                                bgcolor: '#4caf5015',
                                color: '#4caf50',
                                borderRadius: 0.75,
                                fontWeight: 600,
                              }}
                            >
                              {modules.filter(m => m.ParentModuleId === (module.Id || module.id)).length} sub-module{modules.filter(m => m.ParentModuleId === (module.Id || module.id)).length > 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditModule(module)}
                          disabled={saving}
                          sx={{
                            color: '#2196f3',
                            '&:hover': { bgcolor: '#2196f315' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteModule(module)}
                          disabled={saving}
                          sx={{
                            color: '#f44336',
                            '&:hover': { bgcolor: '#f4433615' },
                          }}
                        >
                          {saving ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Module Create/Edit Dialog */}
      <ModuleManagementDialog
        open={moduleDialog.open}
        onClose={handleCloseModuleDialog}
        module={moduleDialog.module}
        mode={moduleDialog.mode}
        onSave={handleSaveModule}
        allModules={modules}
      />
    </>
  );
};

export default ModuleManagementPanel;
